import { StatusBadge } from '@euler/app/components/StatusBadge';
import { IconProps } from '@euler/app/components/icons/types';
import { Typography } from '@euler/app/components/typography';
import { TaskBookmarkStatusView } from '@euler/app/tv/components/TaskBookmarkStatusView';
import { FinishTimeIcon } from '@euler/app/tv/components/dashboard/icons/FinishTimeIcon';
import { ServiceAgentIcon } from '@euler/app/tv/components/dashboard/icons/ServiceAgentIcon';
import { StartTimeIcon } from '@euler/app/tv/components/dashboard/icons/StartTimeIcon';
import { TimeElapsedIcon } from '@euler/app/tv/components/dashboard/icons/TimeElapsedIcon';
import { maskedLicensePlateNo } from '@euler/app/tv/functions/maskedLicensePlateNo';
import { usePresentationTheme } from '@euler/app/tv/theme';
import {
  ColorStop,
  GradientIconProps,
  PresentationTheme,
} from '@euler/app/tv/theme/types';
import { kDefaultVehicleImage } from '@euler/assets';
import { Center } from '@euler/components/Center';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { TaskBookmarkSavePayload, TaskStatusInfo } from '@euler/model';
import {
  formatDurationBetween,
  formatDurationTillNow,
} from '@euler/utils/datetime';
import { formatDateTime } from '@euler/utils/formatters';
import { MotiView } from 'moti';
import { FC, ReactNode, memo, useCallback } from 'react';
import {
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { interpolateColor } from 'react-native-reanimated';

type Props<T = any> = {
  task: T;
  taskNo: string;
  compact?: boolean;
  rowIndex: number;
  colIndex: number;
  width: number;
  height: number;
  horizontal?: boolean;
  vehicleName: string;
  licensePlateNo: string;
  serviceAgentName: string;
  mileage: number;
  score?: number;
  hasObserved?: boolean;
  brandLogoUrl?: string;
  vehicleImageUrl?: string;
  createdAt: string | Date;
  updatedAt?: string | Date;
  finishedAt?: string | Date;
  showFinishedTime?: boolean;
  statusInfo?: TaskStatusInfo;
  showStatus?: boolean;
  bookmarkPayload?: TaskBookmarkSavePayload;
  style?: StyleProp<ViewStyle>;
  onPress?: (task: T) => void;
};

export type TaskCardViewProps = Props;

type ContentProps = Omit<
  Props,
  | 'compact'
  | 'rowIndex'
  | 'colIndex'
  | 'width'
  | 'height'
  | 'horizontal'
  | 'task'
  | 'onPress'
> & {
  theme: PresentationTheme;
};

const kDefaultInitialHorizontalOffset = 200;
const kDefaultInitialVerticalOffset = 100;
const kDefaultHorizontalDelay = 50;
const kDefaultVerticalDelay = 50;
const kMaxDelay = 500;
const kDefaultTransitionDuration = 500;

export const TaskCardView = memo(
  ({
    compact,
    rowIndex,
    colIndex,
    width,
    height,
    horizontal,
    style,
    task,
    onPress,
    ...props
  }: Props) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const {
      dashboard: { card },
    } = theme;
    const delay = horizontal ? kDefaultHorizontalDelay : kDefaultVerticalDelay;
    const delayIndex = horizontal ? colIndex : rowIndex;
    const handlePress = useCallback(() => {
      onPress?.(task);
    }, [onPress, task]);
    return (
      <MotiView
        from={{
          opacity: 0,
          translateX: horizontal ? rpx(kDefaultInitialHorizontalOffset) : 0,
          translateY: horizontal ? 0 : rpx(kDefaultInitialVerticalOffset),
        }}
        animate={{ opacity: 1, translateX: 0, translateY: 0 }}
        delay={Math.min(delay * delayIndex, kMaxDelay)}
        transition={{ type: 'timing', duration: kDefaultTransitionDuration }}
        css={`
          background-color: ${card.bg};
          width: ${width}px;
          height: ${height}px;
          border-radius: ${compact ? height / 2 : rpx(20)}px;
          padding: ${rpx(32)}px;
        `}
        style={style}
      >
        <TouchableOpacity
          css={`
            flex: 1;
            justify-content: ${compact ? 'center' : 'center'};
          `}
          onPress={handlePress}
        >
          {compact ? (
            <CompactView theme={theme} {...props} />
          ) : (
            <DefaultView theme={theme} {...props} />
          )}
        </TouchableOpacity>
      </MotiView>
    );
  },
);

const interpolateScoreColor = (
  score: number | undefined,
  colorStops: ColorStop[],
) => {
  const p = (score ?? 100) / 100;
  return interpolateColor(
    p,
    colorStops.map(x => x[0]),
    colorStops.map(x => x[1]),
  );
};

const DefaultView = memo(
  ({
    theme,
    licensePlateNo,
    vehicleName,
    mileage,
    brandLogoUrl,
    vehicleImageUrl,
    serviceAgentName,
    hasObserved,
    score,
    createdAt,
    updatedAt,
    finishedAt,
    showFinishedTime,
    showStatus,
    statusInfo,
  }: ContentProps) => {
    const {
      dashboard: { card },
      statusBadge,
    } = theme;
    const { rpx } = useResponsiveContext();
    return (
      <>
        {/** license plate no, vehicle name and brand logo */}
        <HStack justifyContent="space-between">
          <View
            css={`
              flex: 1;
            `}
          >
            <HStack justifyContent="flex-start">
              <Typography.Label
                bold
                size={rpx(34)}
                color={card.licensePlateNoTextColor}
                lineHeight={rpx(42)}
              >
                {maskedLicensePlateNo(licensePlateNo)}
              </Typography.Label>
              {showStatus && statusInfo != null && (
                <StatusBadge
                  theme={statusBadge}
                  statusInfo={statusInfo}
                  bordered
                  css={`
                    height: ${rpx(28)}px;
                    margin-left: ${rpx(10)}px;
                  `}
                  textStyle={{
                    fontSize: rpx(16),
                    lineHeight: rpx(24),
                  }}
                />
              )}
              <InlineScoreView
                score={score ?? 100}
                css={`
                  margin-left: ${rpx(24)}px;
                `}
              />
            </HStack>
            <HStack
              justifyContent="flex-start"
              css={`
                margin-top: ${rpx(4)}px;
              `}
            >
              <View
                css={`
                  flex-shrink: 1;
                `}
              >
                <Typography.Label
                  regular
                  size={rpx(20)}
                  color={card.vehicleNameTextColor}
                  lineHeight={rpx(30)}
                  numberOfLines={1}
                >
                  {vehicleName}
                </Typography.Label>
              </View>
              <View
                css={`
                  flex-shrink: 0;
                  padding: 0 ${rpx(9)}px;
                  border-radius: ${rpx(4)}px;
                  border-width: 1px;
                  border-color: ${card.mileageBorderColor};
                  margin-left: ${rpx(10)}px;
                `}
              >
                <Typography.Label
                  light
                  size={rpx(16)}
                  color={card.mileageTextColor}
                  lineHeight={rpx(24)}
                >{`${mileage}公里`}</Typography.Label>
              </View>
            </HStack>
          </View>
          <Img
            uri={brandLogoUrl}
            css={`
              width: ${rpx(40)}px;
              height: ${rpx(40)}px;
            `}
          />
        </HStack>

        {/** horizontal ruler */}
        <View
          css={`
            height: 1px;
            margin-top: ${rpx(20)}px;
            margin-bottom: ${rpx(20)}px;
            background-color: ${card.separatorColor};
          `}
        />

        {/** detailed information */}
        <HStack>
          <View
            css={`
              flex: 1;
            `}
          >
            <InfoRow
              theme={theme}
              icon="serviceAgent"
              value={serviceAgentName}
              suffix={
                hasObserved != null ? (
                  <Center
                    css={`
                      margin-left: ${rpx(8)}px;
                      border-width: 0.5px;
                      border-radius: ${rpx(4)}px;
                      padding: 0 ${rpx(8)}px;
                      height: ${rpx(24)}px;
                      border-color: ${hasObserved
                        ? theme.dashboard.card.observeStatus.observedBorderColor
                        : theme.dashboard.card.observeStatus
                            .unobservedBorderColor};
                    `}
                  >
                    <Typography.Label
                      light
                      size={rpx(13)}
                      lineHeight={rpx(18)}
                      color={
                        hasObserved
                          ? theme.dashboard.card.observeStatus.observedTextColor
                          : theme.dashboard.card.observeStatus
                              .unobservedTextColor
                      }
                    >
                      {hasObserved ? '已关注' : '未关注'}
                    </Typography.Label>
                  </Center>
                ) : null
              }
            />
            <InfoRow
              theme={theme}
              icon="startTime"
              value={formatDateTime(createdAt)}
            />
            {showFinishedTime && (
              <InfoRow
                theme={theme}
                icon="endTime"
                value={formatDateTime(finishedAt ?? updatedAt ?? new Date())}
              />
            )}
            <InfoRow
              theme={theme}
              icon="timeElapsed"
              label={
                showFinishedTime && finishedAt != null ? '服务用时' : undefined
              }
              value={
                showFinishedTime && finishedAt != null
                  ? formatDurationBetween(createdAt, finishedAt)
                  : formatDurationTillNow(createdAt)
              }
            />
          </View>
          <Img
            uri={vehicleImageUrl}
            source={vehicleImageUrl ? undefined : kDefaultVehicleImage}
            resizeMode="contain"
            css={`
              width: 30%;
              height: ${rpx(98)}px;
            `}
          />
        </HStack>
      </>
    );
  },
);

const CompactView = memo(
  ({
    theme,
    licensePlateNo,
    brandLogoUrl,
    statusInfo,
    showStatus,
    score,
    bookmarkPayload,
  }: ContentProps) => {
    const {
      dashboard: { card },
      statusBadge,
    } = theme;

    const { rpx } = useResponsiveContext();

    return (
      <HStack>
        <HStack justifyContent="flex-start">
          {bookmarkPayload != null && (
            <TaskBookmarkStatusView
              payload={bookmarkPayload}
              iconSize={rpx(36)}
              iconColor={theme.task.detail.vehicleBanner.starIconColor}
              outlineIconColor={
                theme.task.detail.vehicleBanner.outlineStarIconColor
              }
              css={`
                margin-right: ${rpx(16)}px;
              `}
            />
          )}
          <Typography.Label
            bold
            size={rpx(34)}
            color={card.licensePlateNoTextColor}
            lineHeight={rpx(42)}
          >
            {maskedLicensePlateNo(licensePlateNo)}
          </Typography.Label>
          {showStatus && statusInfo != null && (
            <StatusBadge
              theme={statusBadge}
              statusInfo={statusInfo}
              bordered
              css={`
                border-width: 0;
                height: ${rpx(42)}px;
              `}
              textStyle={{
                fontSize: rpx(22),
                lineHeight: rpx(28),
              }}
            />
          )}
        </HStack>
        <HStack justifyContent="flex-end">
          {score != null && (
            <InlineScoreView
              score={score ?? 100}
              align="right"
              css={`
                margin-right: ${rpx(24)}px;
              `}
            />
          )}
          <Img
            uri={brandLogoUrl}
            css={`
              width: ${rpx(40)}px;
              height: ${rpx(40)}px;
            `}
          />
        </HStack>
      </HStack>
    );
  },
);

export const TaskDefaultCardView = DefaultView;
export const TaskCompactCardView = CompactView;

type InfoIconType = keyof PresentationTheme['dashboard']['card']['icons'];

const InfoIconMap: {
  [p in InfoIconType]: {
    Icon: FC<IconProps & GradientIconProps>;
    label: string;
  };
} = {
  serviceAgent: {
    Icon: ServiceAgentIcon,
    label: '接车顾问',
  },
  startTime: {
    Icon: StartTimeIcon,
    label: '进店时间',
  },
  endTime: {
    Icon: FinishTimeIcon,
    label: '离店时间',
  },
  timeElapsed: {
    Icon: TimeElapsedIcon,
    label: '在厂时长',
  },
};

const InfoRow = memo(
  ({
    theme,
    icon,
    value,
    label,
    suffix,
  }: {
    theme: PresentationTheme;
    icon: InfoIconType;
    label?: string;
    value: string;
    suffix?: ReactNode;
  }) => {
    const {
      dashboard: { card },
    } = theme;
    const { rpx } = useResponsiveContext();
    const { Icon, label: defaultLabel } = InfoIconMap[icon];
    const iconColors = theme.dashboard.card.icons[icon];
    return (
      <HStack
        justifyContent="flex-start"
        css={`
          margin: ${rpx(4)}px 0;
        `}
      >
        <Icon size={rpx(16)} {...iconColors} />
        <Typography.Label
          light
          color={card.labelColor}
          size={rpx(16)}
          lineHeight={rpx(24)}
          css={`
            margin-left: ${rpx(12)}px;
          `}
        >{`${label ?? defaultLabel}: `}</Typography.Label>
        <Typography.Label
          light
          color={card.valueColor}
          size={rpx(16)}
          lineHeight={rpx(24)}
          numberOfLines={1}
          css={`
            margin-left: ${rpx(8)}px;
          `}
        >
          {value}
        </Typography.Label>
        {suffix}
      </HStack>
    );
  },
);

const InlineScoreView = memo(
  ({
    score,
    align,
    style,
  }: {
    score: number;
    align?: 'left' | 'right';
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const scoreColor = interpolateScoreColor(
      score,
      theme.task.score.innerArcColorStops,
    );
    return (
      <View
        css={`
          height: ${rpx(42)}px;
          overflow: visible;
        `}
        style={style}
      >
        <HStack
          css={`
            position: absolute;
            bottom: 0;
          `}
          style={{
            left: align !== 'right' ? 0 : undefined,
            right: align === 'right' ? 0 : undefined,
          }}
          justifyContent="flex-start"
          alignItems="flex-end"
        >
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Bold};
              font-weight: 700;
              font-size: ${rpx(40)}px;
              line-height: ${rpx(44)}px;
              color: ${scoreColor};
              font-style: italic;
            `}
          >{`${Math.floor(score)}`}</Text>
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Bold};
              font-size: ${rpx(20)}px;
              line-height: ${rpx(36)}px;
              font-style: italic;
              color: ${scoreColor};
            `}
          >
            分
          </Text>
        </HStack>
      </View>
    );
  },
);
